/**
 * firstDocCache — 全局共享的 firstDoc localStorage 缓存（24h TTL）
 *
 * 之前各页面（Pipeline / EarlyRadar）分别用 `pl_pipeline_probes_v1`，
 * 互不复用——访问过 Pipeline 后再去 Trending 或 Watchlist 仍要重新探测。
 *
 * v2 升级为全局共享 key `pl_firstdoc_v2`，所有排行榜共用，跨页命中。
 * 自动从 v1 迁移一次。
 *
 * Entry 形状（与 timeline firstDoc 一致）：
 *   - { date, source, sourceType, url }  — 有文档
 *   - { sourceType: 'no_doc_found', ... } — 已探测但无文档（防止重复探测）
 *   - null                                 — 已探测无任何结果
 */

const FIRSTDOC_LS_KEY = "pl_firstdoc_v2";
// 非对称 TTL：firstDoc.date 是历史事实不变，hit 长缓存；N/A 短缓存自动重探
const FIRSTDOC_HIT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 天（hit 实际不变）
const FIRSTDOC_NA_TTL_MS = 24 * 60 * 60 * 1000;        // 1 天（N/A 1 天后自动重探）
const LEGACY_KEY = "pl_pipeline_probes_v1";

/** 判断一个 entry 是否是 hit（有真实归档日期） */
function isHit(firstDoc) {
  return !!(firstDoc && typeof firstDoc === "object" && firstDoc.date);
}

/** 根据 entry 类型返回 TTL */
function ttlFor(firstDoc) {
  return isHit(firstDoc) ? FIRSTDOC_HIT_TTL_MS : FIRSTDOC_NA_TTL_MS;
}

let _migrated = false;

function safeParse(json) {
  try { return JSON.parse(json || "{}"); } catch { return {}; }
}

function migrateOnce() {
  if (_migrated) return;
  _migrated = true;
  try {
    const current = safeParse(localStorage.getItem(FIRSTDOC_LS_KEY));
    if (Object.keys(current).length > 0) return; // 已有数据，不覆盖
    const legacy = safeParse(localStorage.getItem(LEGACY_KEY));
    const legacyKeys = Object.keys(legacy);
    if (legacyKeys.length === 0) return;
    localStorage.setItem(FIRSTDOC_LS_KEY, JSON.stringify(legacy));
    // 保留 legacy key 以便 PipelinePage 内部 PROBE_LS_KEY 逻辑仍能读到，
    // 等下次大版本时移除
  } catch {}
}

function sweep(saved) {
  const now = Date.now();
  Object.keys(saved).forEach((k) => {
    const entry = saved[k];
    const ttl = ttlFor(entry?.firstDoc);
    if (!entry?.ts || (now - entry.ts) >= ttl) delete saved[k];
  });
  return saved;
}

/**
 * Load all valid firstDoc entries (per-entry TTL — hit 30d, N/A 1d)
 * @returns {{ [name]: { firstDoc, probed: true, ts } }}
 */
export function loadCache() {
  try {
    migrateOnce();
    const saved = safeParse(localStorage.getItem(FIRSTDOC_LS_KEY));
    const now = Date.now();
    const valid = {};
    Object.entries(saved).forEach(([name, entry]) => {
      const ttl = ttlFor(entry?.firstDoc);
      if (entry?.ts && (now - entry.ts) < ttl) {
        valid[name] = {
          firstDoc: entry.firstDoc ?? null,
          probed: true,
          ts: entry.ts,
        };
      }
    });
    return valid;
  } catch {
    return {};
  }
}

/**
 * 持久化单个 firstDoc 探测结果。null 也写入（防止重复探测）。
 */
export function saveCacheEntry(name, firstDoc) {
  if (!name) return;
  try {
    const saved = sweep(safeParse(localStorage.getItem(FIRSTDOC_LS_KEY)));
    saved[name] = { firstDoc, ts: Date.now() };
    localStorage.setItem(FIRSTDOC_LS_KEY, JSON.stringify(saved));
  } catch {}
}

/**
 * 批量持久化（entries: { [name]: firstDoc | null }）
 */
export function saveCacheBatch(entries) {
  if (!entries || typeof entries !== "object") return;
  try {
    const saved = sweep(safeParse(localStorage.getItem(FIRSTDOC_LS_KEY)));
    const now = Date.now();
    Object.entries(entries).forEach(([name, firstDoc]) => {
      if (!name) return;
      saved[name] = { firstDoc, ts: now };
    });
    localStorage.setItem(FIRSTDOC_LS_KEY, JSON.stringify(saved));
  } catch {}
}

/**
 * 给定一组项目名，返回 { hit: {name: firstDoc | null}, miss: [name] }
 * hit 中包含已探测过的（含 null/no_doc_found）；miss 是真正没缓存的。
 */
export function partitionByCache(names) {
  const cache = loadCache();
  const hit = {};
  const miss = [];
  (names || []).forEach((n) => {
    if (!n) return;
    if (cache[n]) hit[n] = cache[n].firstDoc;
    else miss.push(n);
  });
  return { hit, miss };
}
